import { initializeApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { connectAuthEmulator, getAuth, initializeAuth, inMemoryPersistence } from "firebase/auth";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { initializeAppCheck, getToken, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { callPortalFunction, preparePortalAppCheck } from "./utils/portalErrors";
import { prepareInvoiceSession } from "./utils/spendDocuments";

const useFirebaseEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true";
const emulatorHost = import.meta.env.VITE_FIREBASE_EMULATOR_HOST || "127.0.0.1";
if (useFirebaseEmulators && import.meta.env.PROD) {
  throw new Error("Firebase emulators cannot be enabled in a production build.");
}

const firebaseConfig = {
  apiKey: useFirebaseEmulators ? "demo-api-key" : import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDJn_zi4C2U29HybFH6Wr6jPDQWIEnjOV0",
  authDomain: useFirebaseEmulators ? "localhost" : import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "alkeya-mobile.firebaseapp.com",
  projectId: useFirebaseEmulators ? "demo-alkeya-mobile" : import.meta.env.VITE_FIREBASE_PROJECT_ID || "alkeya-mobile",
  storageBucket: useFirebaseEmulators ? "demo-alkeya-mobile.appspot.com" : import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "alkeya-mobile.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "927449742713",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:927449742713:web:bb4568bcbef567bee903a8",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-V90RK3XB4T",
};

export const app = initializeApp(firebaseConfig);
const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY;
const appChecks = new WeakMap();
function configureAppCheck(instance) {
  if (!useFirebaseEmulators && appCheckSiteKey) appChecks.set(instance, initializeAppCheck(instance, { provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey), isTokenAutoRefreshEnabled: true }));
}
configureAppCheck(app);
const preparePortalRequest = instance => preparePortalAppCheck({
  required: !useFirebaseEmulators,
  configured: appChecks.has(instance),
  getToken: () => getToken(appChecks.get(instance), false),
});
export const db = getFirestore(app);
export const auth = getAuth(app);
export const firebaseEmulatorsEnabled = useFirebaseEmulators;
export const customerPortalEnabled = useFirebaseEmulators || import.meta.env.VITE_CUSTOMER_PORTAL_ENABLED === "true";

if (useFirebaseEmulators) {
  connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, emulatorHost, 8080);
  connectStorageEmulator(getStorage(app), emulatorHost, 9199);
}

let appFunctions;
export function getAppFunctions() {
  if (!appFunctions) {
    appFunctions = getFunctions(app, "us-central1");
    if (useFirebaseEmulators) {
      connectFunctionsEmulator(appFunctions, emulatorHost, 5001);
    }

  }
  return appFunctions;
}

export function callAppPortalFunction(name, data = {}) {
  return callPortalFunction({
    auth, name, data, prepareRequest: () => preparePortalRequest(app),
    invoke: (fn, payload) => httpsCallable(getAppFunctions(), fn)(payload).then(result => result.data),
  });
}

export async function openTravelInstallation(data) {
  await preparePortalRequest(app);
  return httpsCallable(getAppFunctions(), "travelEsimDelivery")({ ...data, action: "OPEN" }).then(result => result.data);
}

export async function prepareInvoiceAccess(kind, actorId) {
  const uid = await prepareInvoiceSession(auth, kind, actorId);
  await preparePortalRequest(app);
  if (auth.currentUser?.uid !== uid) throw Object.assign(new Error("Employee sign-in changed."), { code: "invoice/session-changed" });
  return uid;
}

let portalServices;
let portalEmailSetupAuth;
export function getPortalEmailSetupAuth() {
  if (!portalEmailSetupAuth) {
    const setupApp = initializeApp(firebaseConfig, "portalEmailSetup");
    portalEmailSetupAuth = initializeAuth(setupApp, { persistence: inMemoryPersistence });
    if (useFirebaseEmulators) connectAuthEmulator(portalEmailSetupAuth, `http://${emulatorHost}:9099`, { disableWarnings: true });
  }
  return portalEmailSetupAuth;
}
export function getPortalServices() {
  if (!portalServices) {
    const portalApp = initializeApp(firebaseConfig, "customerPortal");
    configureAppCheck(portalApp);
    const portalAuth = getAuth(portalApp);
    const functions = getFunctions(portalApp, "us-central1");
    if (useFirebaseEmulators) {
      connectAuthEmulator(portalAuth, `http://${emulatorHost}:9099`, { disableWarnings: true });
      connectFunctionsEmulator(functions, emulatorHost, 5001);
      portalAuth.settings.appVerificationDisabledForTesting = true;
    }
    portalServices = { auth: portalAuth, functions, prepareRequest: () => preparePortalRequest(portalApp) };
  }
  return portalServices;
}
